import React, { useEffect, useState } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/ui/FormField";
import useAuth from "../hooks/useAuth";
import { useSeason } from "../context/SeasonContext";
import { FiRefreshCw } from "react-icons/fi";
import "./Collection.css"; // Reuse the css styles from collection

export default function Sales() {
  const API = "https://maize.norissolutions.in/Php/public/api";
  const token = sessionStorage.getItem("maaze_token");
  const { refreshToken, user } = useAuth();
  const { selectedSeason } = useSeason();

  /* ================= STATE ================= */

  const [sales, setSales] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [form, setForm] = useState({
    ID: null,
    Party: "",
    Material: "",
    Destination: "",
    Vehicle: ""
  });

  /* ================= FETCH DATA ================= */

  const fetchSales = async () => {
    try {
      const res = await axios.get(`${API}/sales-weighbridge`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: user?.id,
          season_id: selectedSeason
        }
      });

      setSales(res.data?.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchSales();
  }, [user?.id, selectedSeason]);

  /* ================= EDIT / SELECT ================= */

  const handleEdit = (sale) => {
    setForm({
      ID: sale.ID || sale.sales_id,
      Party: sale.Party || "",
      Material: sale.Material || "",
      Destination: sale.Destination || "",
      Vehicle: sale.Vehicle || ""
    });
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async () => {
    if (!form.ID) {
      alert("Please select a sales record from the queue to update.");
      return;
    }

    const payload = {
      Party: form.Party,
      Material: form.Material,
      Destination: form.Destination,
      Vehicle: form.Vehicle,
    };

    try {
      await axios.post(`${API}/sales-weighbridge/${form.ID}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      alert("Sales record updated successfully");

      fetchSales();

      setForm({
        ID: null,
        Party: "",
        Material: "",
        Destination: "",
        Vehicle: ""
      });
    } catch (err) {
      console.log(err.response?.data || err);
      alert("Error updating sales record");
    }
  };

  /* ================= UI ================= */

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSales = sales.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sales.length / itemsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div>
      <PageHeader
        title="Sales"
        subtitle="View and manage sales weighbridge records"
      >
        <FiRefreshCw className="page-refresh" onClick={() => {
          refreshToken();
          fetchSales();
        }} />
      </PageHeader>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
        <Card title="Sales Update details">
          {!form.ID && (
             <div style={{ marginBottom: 15, padding: 10, background: 'rgba(255, 193, 7, 0.1)', color: '#856404', borderRadius: 4, fontSize: 14 }}>
               Please select a sales record from the queue below to edit its details.
             </div>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '15px', opacity: form.ID ? 1 : 0.5, pointerEvents: form.ID ? 'auto' : 'none' }}>
            <FormField
              label="Selected Weighbridge ID"
              type="text"
              value={form.ID || ""}
              disabled
              placeholder="Select from queue"
            />

            <FormField
              label="Party"
              type="text"
              value={form.Party}
              onChange={(e) => setForm({ ...form, Party: e.target.value })}
            />

            <FormField
              label="Material"
              type="text"
              value={form.Material}
              onChange={(e) => setForm({ ...form, Material: e.target.value })}
            />

            <FormField
              label="Destination"
              type="text"
              value={form.Destination}
              onChange={(e) => setForm({ ...form, Destination: e.target.value })}
            />

            <FormField
              label="Vehicle"
              type="text"
              value={form.Vehicle}
              onChange={(e) => setForm({ ...form, Vehicle: e.target.value })}
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Button onClick={handleSubmit} disabled={!form.ID}>Update Sale</Button>

            <Button variant="outline" onClick={() => setForm({
              ID: null, Party: "", Material: "", Destination: "", Vehicle: ""
            })}>
              Clear
            </Button>
          </div>
        </Card>

        <Card title="Sales Queue">
          <div style={{ overflowX: 'auto' }}>
            {currentSales.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', backgroundColor: 'var(--surface-50)' }}>
                    {Object.keys(currentSales[0]).map(key => (
                      <th key={key} style={{ padding: '10px', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--text-muted)' }}>{key}</th>
                    ))}
                    <th style={{ padding: '10px', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--text-muted)' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSales.map((sale, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      {Object.keys(currentSales[0]).map(key => (
                        <td key={key} style={{ padding: '10px', whiteSpace: 'nowrap' }}>{sale[key] !== null ? String(sale[key]) : ""}</td>
                      ))}
                      <td style={{ padding: '10px' }}>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(sale)}>
                          Update
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>

          {/* Pagination Controls */}
          {sales.length > itemsPerPage && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span style={{ fontSize: "14px", color: "var(--text-muted)" }}>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}

          {sales.length === 0 && (
            <div style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)" }}>
              No sales records found.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
